import { useEffect, useState, type ReactNode } from 'react';
import {
  deleteAsignacionInsumosVehiculo,
  getAsignacionesInsumosVehiculos,
  getAsignacionesInsumosVehiculosPaginadas,
  exportarAsignaciones,
  getExportarAsignacionesStatus,
  descargarExportacionAsignaciones,
} from '../../lib';
import type { AsignacionInsumosVehiculo } from '../../lib/appTypes';
import { SectionSurface } from '../../components/role-ui';
import FormAsignacionInsumosVehiculo from './FormAsignacionInsumosVehiculo';
import { Modal, confirmar, SkeletonRows } from '../../components/ui-kit';

function PanelPopup({
  children,
  onClose,
  maxW = 'max-w-6xl',
}: {
  children: ReactNode;
  onClose: () => void;
  maxW?: string;
}) {
  return (
    <Modal onClose={onClose} maxWidthClassName={maxW}>
      <div className="px-6 pb-6 pt-14">{children}</div>
    </Modal>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-EC', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function formatDateTime(value?: string): string {
if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat('es-EC', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function vehiculoLabel(asignacion: AsignacionInsumosVehiculo): string {
  const partes = [
    asignacion.vehiculoPlaca,
    [asignacion.vehiculoMarca, asignacion.vehiculoModelo].filter(Boolean).join(' '),
  ].filter(Boolean);
  return partes.length > 0 ? partes.join(' · ') : `Vehículo ${asignacion.vehiculo}`;
}

export default function BodegaAsignacionesPanel() {
  const [asignaciones, setAsignaciones] = useState<AsignacionInsumosVehiculo[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingAsignacion, setEditingAsignacion] = useState<AsignacionInsumosVehiculo | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  const [busqueda, setBusqueda] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  const [detalleAsignacion, setDetalleAsignacion] = useState<AsignacionInsumosVehiculo | null>(null);
  const [detallePage, setDetallePage] = useState(1);
  const DETALLE_PAGE_SIZE = 10;

  async function refresh(p = page) {
    try {
      setLoading(true);
      const result = await getAsignacionesInsumosVehiculosPaginadas({
        page: p,
        page_size: pageSize,
        search: busqueda.trim() || undefined,
        fecha_desde: fechaDesde || undefined,
        fecha_hasta: fechaHasta || undefined,
      });
      setAsignaciones(result.items);
      setTotalPages(result.totalPages);
      setTotalCount(result.count);
      setPage(result.page);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudieron cargar las asignaciones.');
    } finally {
      setLoading(false);
    }
  }

  const [totales, setTotales] = useState({ asignado: 0, consumido: 0, saldo: 0 });

  async function loadTotales() {
    try {
      const all = await getAsignacionesInsumosVehiculos({
        page_size: 10000,
        order_by: 'fecha_valor_total_asignado',
        order_up: false,
      });
      setTotales(all.reduce(
        (acc, item) => ({
          asignado: acc.asignado + Number(item.valorTotalAsignado || 0),
          consumido: acc.consumido + Number(item.valorTotalInsumos || 0),
          saldo: acc.saldo + Number(item.saldoDisponible || 0),
        }),
        { asignado: 0, consumido: 0, saldo: 0 },
      ));
    } catch { /* silencioso */ }
  }

  useEffect(() => {
    void refresh(1);
    void loadTotales();
  }, []);

  function handleSearch() {
    setPage(1);
    void refresh(1);
  }

  async function handleLimpiarFiltros() {
    setBusqueda('');
    setFechaDesde('');
    setFechaHasta('');
    setPage(1);
    try {
      setLoading(true);
      const result = await getAsignacionesInsumosVehiculosPaginadas({ page: 1, page_size: pageSize });
      setAsignaciones(result.items);
      setTotalPages(result.totalPages);
      setTotalCount(result.count);
      setPage(1);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudieron cargar las asignaciones.');
    } finally {
      setLoading(false);
    }
  }

  function goToPage(p: number) {
    setPage(p);
    void refresh(p);
  }

  async function handleRefreshAfterAction() {
    await loadTotales();
    await refresh(page);
  }

  async function handleEliminarAsignacion(asignacion: AsignacionInsumosVehiculo) {
    if (!(await confirmar({ titulo: `¿Eliminar la asignación de ${vehiculoLabel(asignacion)}?`, mensaje: 'Esta acción no se puede deshacer.', textoAceptar: 'Eliminar' }))) return;
    try {
      await deleteAsignacionInsumosVehiculo(asignacion.id);
      await handleRefreshAfterAction();
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo eliminar la asignación.');
    }
  }

  // ── Exportar XLSX via Celery ────────────────────────────────────────────────
  const [exportando, setExportando] = useState(false);

  async function handleExportXLSX() {
    if (exportando) return;
    try {
      setExportando(true);
      setDataError('');

      const task = await exportarAsignaciones({
        search: busqueda.trim() || undefined,
        fecha_desde: fechaDesde || undefined,
        fecha_hasta: fechaHasta || undefined,
      });
      if (!task.task_id) {
        throw new Error('No se obtuvo ID de tarea.');
      }

      let attempts = 0;
      const maxAttempts = 60;
      let fileUrl = '';
      let fileName = 'Asignaciones_Insumos.xlsx';

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const status = await getExportarAsignacionesStatus(task.task_id);
        attempts++;

        if (status.status === 'SUCCESS') {
          fileUrl = status.file_url || '';
          fileName = status.file_name || 'Asignaciones_Insumos.xlsx';
          break;
        }
        if (status.status === 'FAILURE') {
          throw new Error(status.error || 'La exportación falló.');
        }
      }

      if (!fileUrl) {
        throw new Error('La exportación no se completó a tiempo. Intenta de nuevo.');
      }

      await descargarExportacionAsignaciones(task.task_id, fileName);
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'Error al exportar asignaciones.');
    } finally {
      setExportando(false);
    }
  }

  const detalleTotalPages = detalleAsignacion ? Math.max(1, Math.ceil(detalleAsignacion.detalles.length / DETALLE_PAGE_SIZE)) : 1;
  const detalleSlice = detalleAsignacion ? detalleAsignacion.detalles.slice((detallePage - 1) * DETALLE_PAGE_SIZE, detallePage * DETALLE_PAGE_SIZE) : [];

  const inputCls = 'w-full rounded-[14px] border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-stone-400 focus:ring-4 focus:ring-stone-100';

  return (
    <>
      <section className="space-y-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => void handleExportXLSX()} disabled={exportando} className="ui-btn ui-btn-info">{exportando ? 'Exportando...' : 'Exportar XLSX'}</button>
            <button type="button" onClick={() => { setEditingAsignacion(null); setShowForm(true); }} className="ui-btn ui-btn-primary">Nueva asignacion</button>
          </div>
        </div>

        {dataError && <div className="rounded-[var(--radius-panel)] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">{dataError}</div>}

        <div className="grid gap-4 xl:grid-cols-4">
          <article className="ui-card p-5"><p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Asignaciones</p><p className="mt-4 text-3xl font-semibold text-stone-950">{totalCount}</p></article>
          <article className="ui-card p-5"><p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Total asignado</p><p className="mt-4 text-3xl font-semibold text-stone-950">{formatCurrency(totales.asignado)}</p></article>
          <article className="ui-card p-5"><p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Total insumos</p><p className="mt-4 text-3xl font-semibold text-stone-950">{formatCurrency(totales.consumido)}</p></article>
          <article className="ui-card p-5"><p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Saldo disponible</p><p className="mt-4 text-3xl font-semibold text-emerald-700">{formatCurrency(totales.saldo)}</p></article>
        </div>

        <SectionSurface className="space-y-5">
          <div className="flex flex-col gap-3">
            <div><h4 className="text-lg font-semibold text-stone-900">Listado de asignaciones</h4><p className="text-sm text-stone-500">Filtra por vehiculo, responsable o rango de fechas.</p></div>
            <div className="ui-filtros">
              <div className="ui-filtro-busqueda"><label className="ui-filtro-label">Buscar por vehiculo o responsable</label><input value={busqueda} onChange={e => setBusqueda(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }} placeholder="Placa, marca, responsable..." className={inputCls} /></div>
              <div><label className="ui-filtro-label">Fecha desde</label><input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} className={inputCls} /></div>
              <div><label className="ui-filtro-label">Fecha hasta</label><input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} className={inputCls} /></div>
              <div className="ui-filtro-acciones"><button type="button" onClick={handleSearch} className="ui-btn ui-btn-dark">Buscar</button><button type="button" onClick={() => void handleLimpiarFiltros()} className="ui-btn ui-btn-secondary">Limpiar</button></div>
            </div>
          </div>

          {loading ? <SkeletonRows etiqueta="Cargando asignaciones..." /> : asignaciones.length === 0 ? <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-10 text-center text-stone-500">No hay asignaciones que coincidan con los filtros.</div> : (
            <>
              <div className="overflow-x-auto">
                <table className="ui-tabla min-w-full border-separate border-spacing-y-2 text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-[0.18em] text-[var(--freisa-text-muted)]"><th className="pb-2 pr-4 font-semibold">Vehiculo</th><th className="pb-2 pr-4 font-semibold">Responsable</th><th className="pb-2 pr-4 font-semibold">Km</th><th className="pb-2 pr-4 font-semibold">Fecha</th><th className="pb-2 pr-4 font-semibold">Asignado</th><th className="pb-2 pr-4 font-semibold">Consumido</th><th className="pb-2 pr-4 font-semibold">Saldo</th><th className="pb-2 pr-4 font-semibold">Insumos</th><th className="pb-2 font-semibold text-right">Acciones</th></tr></thead>
                  <tbody>
                    {asignaciones.map(asignacion => (
                      <tr key={asignacion.id} className="bg-[var(--freisa-bg)]/70 text-[var(--freisa-text)] align-top">
                        <td className="rounded-l-[16px] px-4 py-3"><p className="font-semibold">{vehiculoLabel(asignacion)}</p><p className="mt-1 text-xs text-[var(--freisa-text-soft)]">{asignacion.observacion || 'Sin observacion'}</p></td>
                        <td className="px-4 py-3 text-[var(--freisa-text-soft)]">{asignacion.responsable || '—'}</td>
                        <td className="px-4 py-3 text-[var(--freisa-text-soft)]">{asignacion.kilometraje != null ? asignacion.kilometraje.toLocaleString('es-EC') : '—'}</td>
                        <td className="px-4 py-3 text-[var(--freisa-text-soft)]">{formatDateTime(asignacion.fechaValorTotalAsignado)}</td>
                        <td className="px-4 py-3 font-semibold text-stone-900">{formatCurrency(asignacion.valorTotalAsignado)}</td>
                        <td className="px-4 py-3 font-semibold text-amber-700">{formatCurrency(asignacion.valorTotalInsumos)}</td>
                        <td className="px-4 py-3"><span className={`rounded-full px-3 py-1 text-xs font-semibold ${asignacion.saldoDisponible < 0 ? 'border border-rose-200 bg-rose-50 text-rose-700' : 'border border-emerald-200 bg-emerald-50 text-emerald-700'}`}>{formatCurrency(asignacion.saldoDisponible)}</span></td>
                        <td className="px-4 py-3"><span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-semibold text-stone-700">{asignacion.detalles.length} insumo(s)</span></td>
                        <td className="rounded-r-[16px] px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => { setDetalleAsignacion(asignacion); setDetallePage(1); }} className="ui-btn ui-btn-info ui-btn-sm">Detalles</button>
                            <button type="button" onClick={() => { setEditingAsignacion(asignacion); setShowForm(false); }} className="ui-btn ui-btn-secondary ui-btn-sm">Editar</button>
                            <button type="button" onClick={() => void handleEliminarAsignacion(asignacion)} className="ui-btn ui-btn-danger ui-btn-sm">Eliminar</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-4 py-3 rounded-b-[20px]">
                <span className="text-sm text-stone-500">Pagina {page} de {totalPages} · {totalCount} registro(s)</span>
                <div className="flex gap-2">
                  <button onClick={() => goToPage(page - 1)} disabled={page <= 1 || loading} className="rounded-xl border border-stone-200 bg-white px-3 py-1.5 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50">Anterior</button>
                  <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages || loading} className="ui-btn ui-btn-secondary ui-btn-sm">Siguiente</button>
                </div>
              </div>
            </>
          )}
        </SectionSurface>
      </section>

      {(showForm || editingAsignacion) && (
        <PanelPopup onClose={() => { setShowForm(false); setEditingAsignacion(null); }}>
          <FormAsignacionInsumosVehiculo
            initialAsignacion={editingAsignacion}
            onSaved={() => { setShowForm(false); setEditingAsignacion(null); void handleRefreshAfterAction(); }}
            onCancel={() => { setShowForm(false); setEditingAsignacion(null); }}
          />
        </PanelPopup>
      )}

      {detalleAsignacion && (
        <PanelPopup onClose={() => setDetalleAsignacion(null)} maxW="max-w-4xl">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-stone-900">Detalle de asignacion — {vehiculoLabel(detalleAsignacion)}</h3>
              <p className="text-sm text-stone-500">{formatDateTime(detalleAsignacion.fechaValorTotalAsignado)}{detalleAsignacion.responsable ? ` · Responsable: ${detalleAsignacion.responsable}` : ''}</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-[var(--radius-panel)] border border-stone-200 bg-stone-50 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Total asignado</p><p className="mt-2 text-xl font-bold text-stone-900">{formatCurrency(detalleAsignacion.valorTotalAsignado)}</p></div>
              <div className="rounded-[var(--radius-panel)] border border-stone-200 bg-stone-50 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Total consumido</p><p className="mt-2 text-xl font-bold text-amber-700">{formatCurrency(detalleAsignacion.valorTotalInsumos)}</p></div>
              <div className="rounded-[var(--radius-panel)] border border-stone-200 bg-stone-50 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Saldo</p><p className={`mt-2 text-xl font-bold ${detalleAsignacion.saldoDisponible < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{formatCurrency(detalleAsignacion.saldoDisponible)}</p></div>
            </div>

            {detalleAsignacion.detalles.length === 0 ? (
              <div className="rounded-[16px] border border-dashed border-stone-300 bg-white p-6 text-center text-sm text-stone-500">Sin detalles de insumos.</div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-[16px] border border-stone-200">
                  <table className="ui-tabla min-w-max text-xs text-left">
                    <thead className="bg-stone-100 text-stone-500 uppercase tracking-wider">
                      <tr><th className="px-3 py-2 font-semibold border-b border-stone-200 min-w-[180px]">Insumo</th><th className="px-3 py-2 font-semibold border-b border-stone-200 min-w-[140px]">Proveedor</th><th className="px-3 py-2 font-semibold border-b border-stone-200 min-w-[80px]">Cant.</th><th className="px-3 py-2 font-semibold border-b border-stone-200 min-w-[100px]">P. Unitario</th><th className="px-3 py-2 font-semibold border-b border-stone-200 min-w-[100px]">Total</th></tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100 bg-white">
                      {detalleSlice.map((detalle, idx) => (
                        <tr key={detalle.id ?? idx} className="hover:bg-stone-50 transition-colors">
                          <td className="px-3 py-2 font-medium text-stone-800">{detalle.insumoNombre || '—'}</td>
                          <td className="px-3 py-2 text-stone-500">{detalle.proveedorNombre || '—'}</td>
                          <td className="px-3 py-2 text-stone-700">{detalle.cantidad}</td>
                          <td className="px-3 py-2 text-stone-700">{formatCurrency(detalle.precioUnitario ?? 0)}</td>
                          <td className="px-3 py-2 font-semibold text-stone-800">{formatCurrency(detalle.precioTotal ?? 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {detalleAsignacion.detalles.length > DETALLE_PAGE_SIZE && (
                  <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-4 py-2 rounded-b-[16px]">
                    <span className="text-xs text-stone-500">{detalleAsignacion.detalles.length} insumo(s) · Pag {detallePage} de {detalleTotalPages}</span>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setDetallePage(p => Math.max(1, p - 1))} disabled={detallePage <= 1} className="rounded-xl border border-stone-200 bg-white px-2.5 py-1 text-xs font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50">{'←'} Anterior</button>
                      <button type="button" onClick={() => setDetallePage(p => Math.min(detalleTotalPages, p + 1))} disabled={detallePage >= detalleTotalPages} className="rounded-xl border border-stone-200 bg-white px-2.5 py-1 text-xs font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50">Siguiente {'→'}</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </PanelPopup>
      )}
    </>
  );
}
